import { createTheme } from '@mui/material/styles';

// 施工记账管家 - 2026 克制风主题
// 设计理念：扁平、细边框、小圆角、无彩色阴影、数字等宽对齐（Linear / Vercel / Stripe 系）
// 有意去除：渐变、玻璃拟态 backdrop-filter、hover 上浮 translateY、表格斑马纹
const theme = createTheme({
  palette: {
    primary: {
      main: '#3370FF',
      dark: '#245BDB',
      light: '#E1EAFF',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#00C8A6',
      dark: '#00A88C',
      light: '#D4F5EE',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#16A34A',
      light: '#E8F9ED',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#DC2626',
      light: '#FEE9E7',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#D97706',
      light: '#FFF0DC',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#3370FF',
      light: '#E1EAFF',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2329',
      secondary: '#646A73',
      disabled: '#BBBFC4',
    },
    divider: '#E5E7EB',
    action: {
      hover: 'rgba(31, 35, 41, 0.04)',
      selected: '#F0F4FF',
    },
    // Tailwind 灰阶：与上方 divider、背景色既有的硬编码同源，token 化后页面层统一引用
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
    },
    // 快捷操作分类色：4 个语义色之外维持 7 色区分度的补色（emerald 与 success 有意区分深浅）
    category: {
      violet: { main: '#7C3AED', light: '#EDE9FE' },
      slate: { main: '#64748B', light: '#F1F5F9' },
      cyan: { main: '#0891B2', light: '#CFFAFE' },
      emerald: { main: '#059669', light: '#D1FAE5' },
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Noto Sans SC"',
      '"PingFang SC"',
      '"Microsoft YaHei"',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 700, fontSize: '2.5rem', letterSpacing: '-0.03em', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em', lineHeight: 1.3 },
    h3: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' },
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1.125rem' },
    subtitle1: { fontWeight: 600, fontSize: '1rem' },
    subtitle2: { fontWeight: 500, fontSize: '0.875rem' },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', color: '#646A73', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', color: '#8F959E' },
    button: { fontWeight: 600, fontSize: '0.9375rem', letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 8 },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // 金额、天数、日期数字等宽对齐，便于纵向比较
        body: { fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: 10,
          border: '1px solid #E5E7EB',
        },
        defaultProps: { elevation: 0 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 16px',
          minHeight: 40,
          boxShadow: 'none',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            // 克制的 hover：仅加深底色，不上浮
            filter: 'brightness(0.95)',
          },
        },
        outlined: {
          borderWidth: 1,
          borderColor: '#D1D5DB',
          '&:hover': {
            borderWidth: 1,
            borderColor: '#3370FF',
            backgroundColor: '#F0F4FF',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.8125rem',
          height: 26,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          // 纯白底 + 底部细边框，取代渐变；文字深色由 Layout 控制
          backgroundColor: '#FFFFFF',
          color: '#1F2329',
          borderBottom: '1px solid #E5E7EB',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E5E7EB',
          boxShadow: 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F9FAFB',
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#1F2329',
            borderBottom: '1px solid #E5E7EB',
            fontSize: '0.8125rem',
            padding: '10px 16px',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.12s ease',
          '&:hover': { backgroundColor: '#F9FAFB' },
          // 去斑马纹：偶数行不再单独着色，保持纯白 + hover
          '&:last-child td, &:last-child th': { border: 0 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 16px',
          borderBottom: '1px solid #F3F4F6',
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '1.0625rem', padding: '20px 24px 12px' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          padding: '8px 12px',
          transition: 'background-color 0.12s ease',
          '&.Mui-selected': {
            backgroundColor: '#F0F4FF',
            color: '#3370FF',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#E1EAFF' },
          },
          '&:hover': { backgroundColor: '#F5F6F7' },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            minHeight: 40,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: '#3370FF' },
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundColor: '#F3F4F6' },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E5E7EB',
          boxShadow: 'none',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          '&.Mui-selected': { color: '#3370FF' },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: '#3370FF',
          boxShadow: '0 2px 8px rgba(51,112,255,0.25)',
          '&:hover': {
            backgroundColor: '#245BDB',
            boxShadow: '0 4px 12px rgba(51,112,255,0.3)',
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: { zIndex: 9999 },
      },
    },
  },
});

export default theme;
